import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

import { saveAs } from 'file-saver';
import { NGXLogger } from 'ngx-logger';
import { Scheda } from '../models/scheda.model';
import { Intervento } from '../models/intervento.model';
import { Richiesta } from '../models/richiesta.model';

@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  constructor(private logger: NGXLogger) {}

  exportRichiesteToExcel(
    richieste: Richiesta[],
    filename: string,
    isDownload: boolean,
  ): Blob | null {
    const customHeaders = [
      'ANNO',
      'RICHIESTA',
      'ENTE ESECUTORE',
      'STATO',
      'IMPORTO RICHIESTO',
      'DATA PRESENTAZIONE',
      'IMPORTO CONTRIBUTO',
      'IMPORTO FINANZIATO',
      'IMPORTO RENDICONTATO',
    ];

    const customData = richieste.map((richiesta) => ({
      ANNO: richiesta.anno,
      RICHIESTA: richiesta.id,
      'ENTE ESECUTORE': richiesta.enteEsecutore?.label,
      STATO: richiesta.stato?.descrizione,
      'IMPORTO RICHIESTO': richiesta.importoRichiesto,
      'DATA PRESENTAZIONE': richiesta.presentata
        ? new Date(richiesta.presentata).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : '',
      'IMPORTO CONTRIBUTO': richiesta.importoContributo,
      'IMPORTO FINANZIATO': richiesta.importoFinanziato,
      'IMPORTO RENDICONTATO': richiesta.importoRendicontato,
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(customData, {
      header: [],
    });
    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ['data'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    if (isDownload) {
      // Salva il file Excel
      saveAs(blob, filename);
      return null;
    } else {
      return blob;
    }
  }

  exportInterventiToExcel(
    interventi: Intervento[],
    filename: string,
    isDownload: boolean,
  ): Blob | null {
    const customHeaders = [
      'ANNO',
      'SCHEDA (vecchio codice)',
      'SCHEDA (nuovo codice)',
      'ENTE ESECUTORE',
      'BACINO',
      'COMUNE',
      'TITOLO',
      'OGGETTO',
      'CODICE ATO',
      'STATO',
      'TIPO',
      'PRIORITA',
      'IMPORTO RICHIESTO',
      'IMPORTO RIMODULATO',
      'IMPORTO FINANZIATO',
      'IMPORTO TOTALE RENDICONTATO',
      'RENDICONTATO ACCONTO - DATA',
      'RENDICONTATO ACCONTO - IMPORTO',
      'RENDICONTATO SALDO - DATA',
      'RENDICONTATO SALDO - IMPORTO',
      'LIQUIDAZIONE ACCONTO - DATA',
      'LIQUIDAZIONE ACCONTO - IMPORTO',
      'LIQUIDAZIONE ACCONTO - DETERMINA',
      'LIQUIDAZIONE SALDO - DATA',
      'LIQUIDAZIONE SALDO - IMPORTO',
      'LIQUIDAZIONE SALDO - DETERMINA',
    ];

    const customData = interventi.map((intervento) => {
      const acconto =
        intervento.rendicontazioniSintesi?.find(
          (r) => r.tipo?.toUpperCase() === 'ACCONTO',
        ) ?? null;
      const saldo =
        intervento.rendicontazioniSintesi?.find(
          (r) => r.tipo?.toUpperCase() === 'SALDO',
        ) ?? null;
      const liquidazioneAcconto =
        intervento.liquidazioneSintesi?.find(
          (l) => l.tipo?.toUpperCase() === 'ACCONTO',
        ) ?? null;
      const liquidazioneSaldo =
        intervento.liquidazioneSintesi?.find(
          (l) => l.tipo?.toUpperCase() === 'SALDO',
        ) ?? null;

      const formatData = (d: string | null | undefined): string =>
        d
          ? new Date(d).toLocaleDateString('it-IT', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : '';

      return {
        ANNO: intervento.richiesta?.anno,
        'SCHEDA (vecchio codice)': intervento.scheda?.vecchioCodiceUmvs,
        'SCHEDA (nuovo codice)': intervento.scheda?.codice,
        'ENTE ESECUTORE': intervento.richiesta?.enteEsecutore?.label,
        BACINO: intervento.scheda?.bacino?.denominazione ?? '',
        COMUNE: intervento.scheda?.comune?.denominazione ?? '',
        TITOLO: intervento.scheda?.titolo ?? '',
        OGGETTO: intervento.scheda?.oggettoIntervento?.descrizione ?? '',
        'CODICE ATO': intervento.scheda?.nuovoCodiceAto?.codice || '',
        STATO: intervento.stato?.descrizione,
        TIPO: intervento.tipoIntervento?.descrizione,
        PRIORITA: intervento.priorita,
        'IMPORTO RICHIESTO': intervento.importoRichiesto,
        'IMPORTO RIMODULATO': intervento.importoRimodulato ?? 0,
        'IMPORTO FINANZIATO': intervento.importoFinanziato ?? 0,
        'IMPORTO TOTALE RENDICONTATO': intervento.importoRendicontato ?? 0,
        'RENDICONTATO ACCONTO - DATA': formatData(acconto?.data),
        'RENDICONTATO ACCONTO - IMPORTO': acconto?.importo ?? 0,
        'RENDICONTATO SALDO - DATA': formatData(saldo?.data),
        'RENDICONTATO SALDO - IMPORTO': saldo?.importo ?? 0,
        'LIQUIDAZIONE ACCONTO - DATA': formatData(liquidazioneAcconto?.data),
        'LIQUIDAZIONE ACCONTO - IMPORTO': liquidazioneAcconto?.importo ?? '',
        'LIQUIDAZIONE ACCONTO - DETERMINA':
          liquidazioneAcconto?.numeroDetermina ?? '',
        'LIQUIDAZIONE SALDO - DATA': formatData(liquidazioneSaldo?.data),
        'LIQUIDAZIONE SALDO - IMPORTO': liquidazioneSaldo?.importo ?? '',
        'LIQUIDAZIONE SALDO - DETERMINA':
          liquidazioneSaldo?.numeroDetermina ?? '',
      };
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(customData, {
      header: [],
    });

    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ['data'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    if (isDownload) {
      // Salva il file Excel
      saveAs(blob, filename);
      return null;
    } else {
      return blob;
    }
  }

  exportReportRegioneMensileToExcel(
    interventi: Intervento[],
    anno: number,
    mese: number,
    filename: string,
    isDownload: boolean,
  ): Blob | null {
    const headers = [
      'N. INTERVENTO DI PMO (CODICE NUOVO)',
      'N. INTERVENTO DI PMO (CODICE VECCHIO)',
      'SOTTOBACINO/I INTERESSATO/I',
      "IMPORTO COMPLESSIVO DELL'INTERVENTO REALIZZATO, AL NETTO DEI RIASSI D'ASTA (IVA COMPRESA)",
      'PERIODO DI EFFETTIVA REALIZZAZIONE',
      "SOGGETTO CHE HA REALIZZATO L'INTERVENTO",
      'ATTO AMMINISTRATIVO DI RIFERIMENTO',
      'FREQUENZA DI REALIZZAZIONE',
    ];

    const parseDate = (value: string | null | undefined): Date => {
      if (!value) {
        return new Date('');
      }
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }

      // Fallback per date in formato gg/mm/aaaa
      const parts = value.split('/');
      if (parts.length === 3) {
        const giorno = Number(parts[0]);
        const meseLocal = Number(parts[1]);
        const annoLocal = Number(parts[2]);
        return new Date(annoLocal, meseLocal - 1, giorno);
      }

      return new Date('');
    };

    const formatMeseAnno = (data: string | null | undefined): string => {
      if (!data) {
        return '';
      }
      const parsed = parseDate(data);
      if (Number.isNaN(parsed.getTime())) {
        return '';
      }
      return parsed.toLocaleDateString('it-IT', {
        month: 'short',
        year: '2-digit',
      });
    };

    this.logger.info('[EXCEL SERVICE] Report regione mensile', anno, mese);
    this.logger.info('[EXCEL SERVICE] Interventi', interventi.length);
    type ReportRegioneMensileRow = Record<
      (typeof headers)[number],
      string | number
    >;

    const rows: ReportRegioneMensileRow[] = interventi.flatMap((intervento) => {
      const liquidazioni = intervento.liquidazioneSintesi ?? [];

      const liquidazioniFiltrate = liquidazioni.filter((liquidazione) => {
        const dataLiquidazione = parseDate(liquidazione.data);
        this.logger.info(
          '[EXCEL SERVICE] Liquidazione',
          liquidazione.numeroDetermina +
            ' - ' +
            dataLiquidazione.toLocaleDateString('it-IT', {
              month: 'short',
              year: '2-digit',
            }),
        );

        const stessoAnno = dataLiquidazione.getFullYear() === anno;
        const stessoMese = dataLiquidazione.getMonth() + 1 === mese;
        /*  this.logger.info(
          '[EXCEL SERVICE] Stesso anno e mese',
          stessoAnno,
          dataLiquidazione.getMonth(),
          ' Vs ' + mese,
          stessoAnno && stessoMese,
        ); */

        return stessoAnno && stessoMese;
      });
      this.logger.info(
        '[EXCEL SERVICE] Liquidazioni filtrate',
        liquidazioniFiltrate.length,
      );
      if (liquidazioniFiltrate.length) {
        return liquidazioniFiltrate.map((liquidazione) => ({
          'N. INTERVENTO DI PMO (CODICE NUOVO)':
            intervento.scheda?.codice ?? '',
          'N. INTERVENTO DI PMO (CODICE VECCHIO)':
            intervento.scheda?.vecchioCodiceUmvs ?? '',
          'SOTTOBACINO/I INTERESSATO/I':
            intervento.scheda?.bacino?.denominazione ?? '',
          "IMPORTO COMPLESSIVO DELL'INTERVENTO REALIZZATO, AL NETTO DEI RIASSI D'ASTA (IVA COMPRESA)":
            liquidazione.importo ?? '',
          'PERIODO DI EFFETTIVA REALIZZAZIONE': '',
          "SOGGETTO CHE HA REALIZZATO L'INTERVENTO":
            intervento.richiesta?.enteEsecutore?.label ?? '',
          'ATTO AMMINISTRATIVO DI RIFERIMENTO':
            liquidazione.numeroDetermina ?? '',
          'FREQUENZA DI REALIZZAZIONE': intervento.scheda?.frequenza ?? '',
        }));
      }

      const dataLiquidazione = parseDate(intervento.dataLiquidazione);
      const stessoAnno = dataLiquidazione.getFullYear() === anno;
      const stessoMese = dataLiquidazione.getMonth() + 1 === mese;
      if (!stessoAnno || !stessoMese) {
        return [];
      }

      return [
        {
          'N. INTERVENTO DI PMO (CODICE NUOVO)':
            intervento.scheda?.codice ?? '',
          'N. INTERVENTO DI PMO (CODICE VECCHIO)':
            intervento.scheda?.vecchioCodiceUmvs ?? '',
          'SOTTOBACINO/I INTERESSATO/I':
            intervento.scheda?.bacino?.denominazione ?? '',
          "IMPORTO COMPLESSIVO DELL'INTERVENTO REALIZZATO, AL NETTO DEI RIASSI D'ASTA (IVA COMPRESA)":
            intervento.importoLiquidato ?? '',
          'PERIODO DI EFFETTIVA REALIZZAZIONE': '',
          "SOGGETTO CHE HA REALIZZATO L'INTERVENTO":
            intervento.richiesta?.enteEsecutore?.label ?? '',
          'ATTO AMMINISTRATIVO DI RIFERIMENTO': '',
          'FREQUENZA DI REALIZZAZIONE': intervento.scheda?.frequenza ?? '',
        },
      ];
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers]);
    XLSX.utils.sheet_add_json(worksheet, rows, {
      origin: 'A2',
      skipHeader: true,
    });
    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ['data'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    if (isDownload) {
      saveAs(blob, filename);
      return null;
    }
    return blob;
  }

  exportReportRegioneAnnualeToExcel(
    interventi: Intervento[],
    annoRiferimento: number,
    filename: string,
    isDownload: boolean,
  ): Blob | null {
    const parseDate = (value: string | null | undefined): Date => {
      if (!value) {
        return new Date('');
      }
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }

      const parts = value.split('/');
      if (parts.length === 3) {
        const giorno = Number(parts[0]);
        const meseLocal = Number(parts[1]);
        const annoLocal = Number(parts[2]);
        return new Date(annoLocal, meseLocal - 1, giorno);
      }

      return new Date('');
    };

    const headers = [
      'N. INTERVENTO DI PMO (CODICE NUOVO)',
      'N. INTERVENTO DI PMO (CODICE VECCHIO)',
      "SOGGETTO ATTUATORE DELL'INTERVENTO",
      "SOGGETTO REALIZZATORE DELL'INTERVENTO",
      'TIPOLOGIA DI CUI DEL. ATO N. 465/2012',
      'CODICE TIPOLOGIA DI INTERVENTO',
      'PROGRAMMATO/NON PROGRAMMATO',
      'COMUNE/I',
      'SOTTOBACINO/I INTERESSATO/I',
      "OGGETTO DELL'INTERVENTO",
      /*  'IMPORTO LAVORI DA PMO (EUR)',
      'IMPORTO LAVORI AGGIORNATO (EUR)', */
      `CONSUNTIVO LAVORI ANNO ${annoRiferimento} (EUR)`,
      'ATTO AMMINISTRATIVO DI RIFERIMENTO',
      'ANNO DI RIFERIMENTO LAVORI',
    ];

    const rows = interventi.flatMap((intervento) => {
      const liquidazioni = intervento.liquidazioneSintesi ?? [];
      const liquidazioniAnno = liquidazioni.filter((liquidazione) => {
        const data = parseDate(liquidazione.data);
        return (
          !Number.isNaN(data.getTime()) &&
          data.getFullYear() === annoRiferimento
        );
      });

      const dataLiquidazioneIntervento = parseDate(intervento.dataLiquidazione);
      const haDataLiquidazioneIntervento =
        !Number.isNaN(dataLiquidazioneIntervento.getTime()) &&
        dataLiquidazioneIntervento.getFullYear() === annoRiferimento;

      if (!liquidazioniAnno.length && !haDataLiquidazioneIntervento) {
        return [];
      }

      const attoRiferimento = liquidazioniAnno
        .map((liquidazione) => liquidazione.numeroDetermina ?? '')
        .filter((numero) => numero.trim().length > 0)
        .join(' | ');

      const consuntivo = liquidazioniAnno.length
        ? liquidazioniAnno.reduce(
            (totale, liquidazione) => totale + (liquidazione.importo ?? 0),
            0,
          )
        : (intervento.importoLiquidato ?? 0);
      this.logger.info('Scheda', intervento.scheda?.codiceAto);

      return [
        {
          'N. INTERVENTO DI PMO (CODICE NUOVO)':
            intervento.scheda?.codice ?? '',
          'N. INTERVENTO DI PMO (CODICE VECCHIO)':
            intervento.scheda?.vecchioCodiceUmvs ?? '',
          "SOGGETTO ATTUATORE DELL'INTERVENTO": 'UNIONE MONTANA VALLE SUSA',
          "SOGGETTO REALIZZATORE DELL'INTERVENTO":
            intervento.richiesta?.enteEsecutore?.label ?? '',
          'TIPOLOGIA DI CUI DEL. ATO N. 465/2012':
            intervento.scheda?.codiceAto?.codice ?? '',
          'CODICE TIPOLOGIA DI INTERVENTO':
            intervento.scheda?.nuovoCodiceAto?.codice ?? '',
          'PROGRAMMATO/NON PROGRAMMATO': 'PROGRAMMATO',
          'COMUNE/I': intervento.scheda?.comune?.denominazione ?? '',
          'SOTTOBACINO/I INTERESSATO/I':
            intervento.scheda?.bacino?.denominazione ?? '',
          "OGGETTO DELL'INTERVENTO":
            intervento.scheda?.oggettoIntervento?.descrizione ?? '',
          /*  'IMPORTO LAVORI DA PMO (EUR)': intervento.importoRendicontato ?? '',
          'IMPORTO LAVORI AGGIORNATO (EUR)': intervento.importoLiquidato ?? '', */
          [`CONSUNTIVO LAVORI ANNO ${annoRiferimento} (EUR)`]: consuntivo,
          'ATTO AMMINISTRATIVO DI RIFERIMENTO': attoRiferimento,
          'ANNO DI RIFERIMENTO LAVORI': intervento.richiesta?.anno ?? '',
        },
      ];
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers]);
    XLSX.utils.sheet_add_json(worksheet, rows, {
      origin: 'A2',
      skipHeader: true,
    });

    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ['data'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    if (isDownload) {
      saveAs(blob, filename);
      return null;
    }
    return blob;
  }

  exportSchedeToExcel(
    schede: Scheda[],
    filename: string,
    isDownload: boolean,
  ): Blob | null {
    const customHeaders = [
      'NUOVO CODICE PMO',
      'VECCHIO CODICE PMO',

      'BACINO',
      'COMUNE',
      'RIO/LOCALITA',
      'OGGETTO INTERVENTO',
      'CODICE ATO',
      'CODICE TIPOLOGIA INTERVENTO',
      'FREQUENZA',
      'ACCESSIBILITA',
      'ANNO ULTIMO INTERVENTO',
      'ANNO PROSSIMO INTERVENTO',
      'IMPORTO ULTIMO INTERVENTO',
      'NOTE',
    ];

    const customData = schede.map((scheda) => ({
      'NUOVO CODICE': scheda.codice,
      'VECCHIO CODICE PMO': scheda.vecchioCodiceUmvs,

      BACINO: scheda.bacino.denominazione,
      COMUNE: scheda.comune.denominazione,
      'RIO/LOCALITA': scheda.rioLocalita,
      'OGGETTO INTERVENTO':
        scheda.oggettoIntervento.codice +
        ' - ' +
        scheda.oggettoIntervento.descrizione,
      'CODICE ATO': scheda.codiceAto?.codice || '',
      'CODICE TIPOLOGIA INTERVENTO': scheda.nuovoCodiceAto?.codice || '',
      FREQUENZA: scheda.frequenza,
      ACCESSIBILITA: scheda.accessibilita,
      'ANNO ULTIMO INTERVENTO': scheda.annoUltimoIntervento,
      'ANNO PROSSIMO INTERVENTO': scheda.annoProssimoIntervento,
      'IMPORTO ULTIMO INTERVENTO': scheda.importoUltimoIntervento,
      NOTE: scheda.note,
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(customData, {
      header: [],
    });
    worksheet['A1'] = { v: customHeaders[0] };
    worksheet['B1'] = { v: customHeaders[1] };
    worksheet['C1'] = { v: customHeaders[2] };
    worksheet['D1'] = { v: customHeaders[3] };
    worksheet['E1'] = { v: customHeaders[4] };

    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ['data'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    if (isDownload) {
      // Salva il file Excel
      saveAs(blob, filename);
      return null;
    } else {
      return blob;
    }
  }
}
